import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

/** Landing-page "See past classes" gallery preview. Shows the 6 newest photos
 *  if any are published; otherwise renders nothing so the section disappears. */
export default function GalleryPreviewSection() {
  const { data: photos = [] } = useQuery({
    queryKey: ["galleryPreview"],
    queryFn: async () => (await api.get("/gallery/photos?limit=6")).data,
    staleTime: 5 * 60 * 1000,
  });

  if (!photos || photos.length === 0) return null;

  return (
    <section
      className="bg-gg-surface border-y border-gg-border"
      data-testid="gallery-preview-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-gg-brown font-semibold">
              From past classes
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl text-[var(--gg-text)] mt-2 max-w-2xl">
              See what kids actually do here.
            </h2>
          </div>
          <Link
            to="/gallery"
            className="inline-flex items-center gap-1 text-sm text-gg-green hover:text-gg-brown font-semibold"
            data-testid="gallery-preview-see-all"
          >
            See full gallery <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
          {photos.map((p, idx) => (
            <Link
              key={p.id}
              to={`/gallery${p.class_id ? `?class=${p.class_id}` : ""}`}
              className={`relative overflow-hidden rounded-2xl border border-gg-border bg-gg-bg group ${
                idx === 0 ? "col-span-2 row-span-2 aspect-square sm:aspect-auto" : "aspect-square"
              }`}
              data-testid={`gallery-preview-photo-${p.id}`}
            >
              <img
                src={p.image_url}
                alt={p.alt_text || p.caption || "Past class moment"}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
